import { Injectable } from '@angular/core';
import { NavigationEnd, PRIMARY_OUTLET, Router, UrlSegmentGroup, UrlTree } from '@angular/router';
import { filter } from 'rxjs';

/**
 * The kind of thing a search result points at. Agents live under `/a/:id`,
 * offices and franchisors (groups) both live under `/o/:id`.
 */
export type SearchTargetType = 'agent' | 'group';

// Sub-sections of an agent page (`/a/:id/<sub>`) that a search selection can
// meaningfully carry over. Mirrors the child routes in
// agent-feature-routing.module.ts (the empty default dashboard route is not
// listed - that is represented by "no sub-section").
const AGENT_SUBSECTIONS: readonly string[] = ['documents', 'settings'];

// Sub-sections of a group page (`/o/:id/<sub>`) that a search selection can
// carry over. Mirrors the child routes in group-feature-routing.module.ts.
const GROUP_SUBSECTIONS: readonly string[] = [
  'staff',
  'offices',
  'reports',
  'documents',
  'settings',
];

/**
 * Tracks the sub-section of the agent/group page the user is currently on so
 * that selecting another item from search can route to the equivalent page.
 *
 * For example, while viewing a user's settings page (`/a/123/settings`),
 * searching for and selecting another user routes to that user's settings page
 * (`/a/456/settings`) instead of their dashboard. The same applies to a user's
 * documents page and to group pages.
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationContextService {
  // The current sub-section, e.g. 'settings' or 'documents'. An empty string
  // means the user is on a dashboard/root page or somewhere that is not an
  // agent/group page at all.
  private currentSubSection = '';

  constructor(private router: Router) {
    this.updateFromUrl(this.router.url);

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.updateFromUrl(event.urlAfterRedirects));
  }

  /**
   * Builds a router link for a selected search result, preserving the current
   * page context. When the user is viewing a sub-section (e.g. settings,
   * documents) and the target supports that same sub-section, the link points
   * at the target's matching sub-section; otherwise it points at the target's
   * root page.
   */
  getContextualLink(target: SearchTargetType, id: string): string[] {
    const base = target === 'agent' ? ['/a', id] : ['/o', id];
    const supported = target === 'agent' ? AGENT_SUBSECTIONS : GROUP_SUBSECTIONS;

    if (this.currentSubSection && supported.includes(this.currentSubSection)) {
      return [...base, this.currentSubSection];
    }

    return base;
  }

  private updateFromUrl(url: string): void {
    const segments = this.primarySegmentPaths(this.router.parseUrl(url));

    // The search results page is transparent to context: reaching it (e.g. via
    // "See all results") should not clear the sub-section, so that selecting a
    // result from there still routes to the equivalent page of the item the
    // user was viewing before they searched.
    if (segments[0] === 'search') {
      return;
    }

    this.currentSubSection = this.extractSubSection(segments);
  }

  private extractSubSection(segments: string[]): string {
    // Expect `/a/:id/<sub>` or `/o/:id/<sub>`; the sub-section is the third
    // primary segment. Anything shorter is a root/dashboard page.
    const prefix = segments[0];
    if (prefix !== 'a' && prefix !== 'o') {
      return '';
    }

    return segments[2] ?? '';
  }

  /**
   * The paths of the primary route, flattened across segment groups.
   *
   * Opening a modal nests the rest of the primary route one group deeper:
   * `/o/1/staff` becomes `/o/1/(staff//modal:notes)`, because the modal outlet
   * is navigated relative to the parent and so is a sibling of `staff` rather
   * than a child of it. Reading only the top-level group would see `[o, 1]`
   * there and conclude the user was on a dashboard, losing the sub-section for
   * as long as any modal was open. Walking the primary chain reads both URL
   * shapes the same way, and still ignores auxiliary outlets — which is what
   * lets a modal over settings keep reading as 'settings'.
   */
  private primarySegmentPaths(tree: UrlTree): string[] {
    const paths: string[] = [];

    for (
      let group: UrlSegmentGroup | undefined = tree.root.children[PRIMARY_OUTLET];
      group;
      group = group.children[PRIMARY_OUTLET]
    ) {
      paths.push(...group.segments.map(segment => segment.path));
    }

    return paths;
  }
}
